<template>
    <div class="rag-settings">
        <div class="section-title">{{ $t("chatbotv2.ragMethod") }}</div>
        <div class="rag-method-cards">
            <div
                class="rag-card"
                :class="{ selected: ragMethod === 'openai-rag' }"
                @click="selectRagMethod('openai-rag')"
            >
                <div class="rag-card-header">
                    <img src="https://openai.com/favicon.ico" class="rag-logo" onerror="this.style.display='none'" />
                    <div class="rag-card-title">{{ $t("chatbotv2.openaiRag") }}</div>
                    <a-tag v-if="ragMethod === 'openai-rag'" color="green">{{ $t("chatbotv2.active") }}</a-tag>
                </div>
                <div class="rag-card-desc">
                    {{ $t("chatbotv2.openaiRagDesc") }}
                </div>
            </div>
            <div
                class="rag-card"
                :class="{ selected: ragMethod === 'supsis-rag' }"
                @click="selectRagMethod('supsis-rag')"
            >
                <div class="rag-card-header">
                    <div class="rag-card-title">{{ $t("chatbotv2.supsisRag") }}</div>
                    <a-tag color="blue">{{ $t("chatbotv2.new") }}</a-tag>
                    <a-tag v-if="ragMethod === 'supsis-rag'" color="green">{{ $t("chatbotv2.active") }}</a-tag>
                </div>
                <div class="rag-card-desc">
                    {{ $t("chatbotv2.supsisRagDesc") }}
                </div>
            </div>
        </div>

        <div v-if="ragMethod === 'supsis-rag'" class="supsis-rag-config">
            <div class="config-section">
                <div class="section-title">{{ $t("chatbotv2.openRouterChannel") }} <span class="required">*</span></div>
                <div class="section-desc">{{ $t("chatbotv2.openRouterChannelDesc") }}</div>
                <a-select
                    v-model="openrouterChannel"
                    :placeholder="$t('chatbotv2.selectOpenRouterChannel')"
                    style="width: 100%"
                    @change="saveOpenrouterChannel"
                >
                    <a-select-option v-for="ch in openrouterChannels" :key="ch._id" :value="ch._id">
                        {{ ch.name }}
                        <span v-if="ch.payload && ch.payload.keyMode === 'byok'" style="color:#1890ff">{{
                            $t("chatbotv2.ragChannelBadgeByok")
                        }}</span>
                        <span v-else style="color: #52c41a">{{ $t("chatbotv2.ragChannelBadgeSupsis") }}</span>
                    </a-select-option>
                </a-select>
                <div v-if="openrouterChannels.length === 0" class="no-channel-warn">
                    <a-alert type="warning" show-icon :message="$t('chatbotv2.noOpenRouterChannel')" :description="$t('chatbotv2.createOpenRouterChannelDesc')" />
                </div>
            </div>

            <div class="config-section">
                <div class="section-title">{{ $t("chatbotv2.embeddingModel") }}</div>
                <div class="section-desc">{{ $t("chatbotv2.embeddingModelDesc") }}</div>
                <div class="embedding-model-cards">
                    <div
                        v-for="em in embeddingModels"
                        :key="em.id"
                        class="embedding-card"
                        :class="{ selected: embeddingModel === em.id }"
                        @click="selectEmbeddingModel(em.id)"
                    >
                        <div class="embedding-card-header">
                            <span class="model-name">{{ em.name }}</span>
                            <a-tag v-if="em.recommended" color="green">{{ $t("chatbotv2.recommended") }}</a-tag>
                        </div>
                        <div class="model-meta">
                            <span>{{ em.contextLength }} token</span>
                            <span>{{ em.dimensions }} boyut</span>
                        </div>
                        <div class="model-desc">{{ em.description }}</div>
                    </div>
                </div>
            </div>
        </div>

        <div class="save-info" v-if="saved">
            <a-alert type="success" message="RAG ayarları kaydedildi." :after-close="() => saved = false" closable />
        </div>
    </div>
</template>
<script>
import Vue from "vue";
import { sendSocketMessage } from "../../../../utils/socketStream";

const EMBEDDING_MODELS = [
    {
        id: "voyage-4-lite",
        name: "voyage-4-lite",
        recommended: false,
        contextLength: "32,000",
        dimensions: "1024",
        description: "Düşük maliyet, yüksek hız. Yüksek hacimli uygulamalar için optimize edilmiştir.",
    },
    {
        id: "voyage-4",
        name: "voyage-4",
        recommended: true,
        contextLength: "32,000",
        dimensions: "1024",
        description: "Dengelenmiş performans. Genel metin araması için önerilen model.",
    },
    {
        id: "voyage-4-large",
        name: "voyage-4-large",
        recommended: false,
        contextLength: "32,000",
        dimensions: "1024",
        description: "Maksimum doğruluk. Karmaşık semantik ilişkiler için idealdir.",
    },
    {
        id: "voyage-code-3",
        name: "voyage-code-3",
        recommended: false,
        contextLength: "32,000",
        dimensions: "1024",
        description: "Kod araması ve teknik dokümantasyon için optimize edilmiştir.",
    },
];

export default Vue.extend({
    name: "RAGSettings",
    props: {
        storyId: { type: String, required: true },
    },
    data() {
        return {
            ragMethod: "openai-rag",
            openrouterChannel: null,
            embeddingModel: "voyage-4",
            embeddingModels: EMBEDDING_MODELS,
            openrouterChannels: [],
            saved: false,
        };
    },
    methods: {
        async loadStorySettings() {
            const story = this.$store.state.CB.stories.find((s) => s._id === this.storyId);
            if (story && story.ai) {
                this.ragMethod = story.ai.ragMethod || "openai-rag";
                this.embeddingModel = story.ai.embeddingModel || "voyage-4";
                this.openrouterChannel = story.ai.openrouterChannel || null;
            }
        },
        loadOpenRouterChannels() {
            const channels = this.$store.state.channel.channels || [];
            this.openrouterChannels = channels.filter(function(c) {
                return c.type === "openrouter" && !c.isDeleted;
            });
        },
        async selectRagMethod(method) {
            const story = this.$store.state.CB.stories.find((s) => s._id === this.storyId);
            const prev = (story && story.ai && story.ai.ragMethod) || "openai-rag";
            if (method === prev) {
                return;
            }
            this.ragMethod = method;
            await this.$store.dispatch("CB/updateStorySingleKey", { id: this.storyId, key: "ai.ragMethod", value: method });
            if (method === "supsis-rag" && prev === "openai-rag") {
                sendSocketMessage("chatbot/v2 supsis-rag-migrate-kb", {
                    siteId: this.$store.state.site.site._id,
                    storyId: this.storyId,
                });
                this.$message.info(this.$t("chatbotv2.supsisRagMigrateStarted"));
            }
            this.saved = true;
        },
        async selectEmbeddingModel(modelId) {
            this.embeddingModel = modelId;
            await this.$store.dispatch("CB/updateStorySingleKey", { id: this.storyId, key: "ai.embeddingModel", value: modelId });
            this.saved = true;
        },
        async saveOpenrouterChannel(channelId) {
            this.openrouterChannel = channelId;
            await this.$store.dispatch("CB/updateStorySingleKey", { id: this.storyId, key: "ai.openrouterChannel", value: channelId });
            this.saved = true;
        },
    },
    mounted() {
        this.loadStorySettings();
        this.loadOpenRouterChannels();
    },
    watch: {
        storyId() {
            this.loadStorySettings();
        },
    },
});
</script>

<style scoped lang="scss">
.rag-settings {
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 24px;
    max-width: 860px;

    .section-title {
        font-size: 15px;
        font-weight: 600;
        color: #22272d;
        margin-bottom: 8px;

        .required {
            color: red;
        }
    }
    .section-desc {
        font-size: 13px;
        color: #666;
        margin-bottom: 12px;
    }

    .rag-method-cards {
        display: flex;
        gap: 16px;
    }

    .rag-card {
        flex: 1;
        border: 2px solid #e5e5e5;
        border-radius: 12px;
        padding: 18px;
        cursor: pointer;
        transition: all 0.2s;
        background: #fff;

        &:hover {
            border-color: #1890ff;
        }

        &.selected {
            border-color: #1890ff;
            background: #e6f7ff;
        }

        .rag-card-header {
            display: flex;
            align-items: center;
            gap: 8px;
            margin-bottom: 8px;

            .rag-logo {
                width: 20px;
                height: 20px;
            }

            .rag-card-title {
                font-size: 15px;
                font-weight: 600;
            }
        }

        .rag-card-desc {
            font-size: 13px;
            color: #555;
            line-height: 1.5;
        }
    }

    .supsis-rag-config {
        display: flex;
        flex-direction: column;
        gap: 24px;
    }

    .config-section {
        background: #fafafa;
        border-radius: 12px;
        padding: 18px;
        border: 1px solid #e5e5e5;
    }

    .no-channel-warn {
        margin-top: 10px;
    }

    .embedding-model-cards {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 12px;
    }

    .embedding-card {
        border: 2px solid #e5e5e5;
        border-radius: 10px;
        padding: 14px;
        cursor: pointer;
        transition: all 0.2s;
        background: #fff;

        &:hover {
            border-color: #1890ff;
        }

        &.selected {
            border-color: #1890ff;
            background: #e6f7ff;
        }

        .embedding-card-header {
            display: flex;
            align-items: center;
            gap: 8px;
            margin-bottom: 6px;

            .model-name {
                font-weight: 600;
                font-size: 13px;
                font-family: monospace;
            }
        }

        .model-meta {
            display: flex;
            gap: 10px;
            font-size: 11px;
            color: #888;
            margin-bottom: 6px;
        }

        .model-desc {
            font-size: 12px;
            color: #555;
            line-height: 1.4;
        }
    }

    .save-info {
        margin-top: 8px;
    }
}
</style>
